import React from 'react';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

interface SortableHeaderProps {
  label: string;
  sortKey: string;
  currentSortBy: string;
  currentSortOrder: string;
  onSort: (sortBy: string, sortOrder: string) => void;
}

const SortableHeader: React.FC<SortableHeaderProps> = ({
  label,
  sortKey,
  currentSortBy,
  currentSortOrder,
  onSort,
}) => {
  const handleClick = () => {
    if (currentSortBy === sortKey) {
      // Toggle sort order if same field
      const newOrder = currentSortOrder === 'ASC' ? 'DESC' : 'ASC';
      onSort(sortKey, newOrder);
    } else {
      // Default to DESC for date fields (most recent first)
      onSort(sortKey, 'DESC');
    }
  };

  const renderSortIcon = () => {
    if (currentSortBy !== sortKey) {
      return (
        <span className="ml-1 text-gray-400 opacity-50">
          <ChevronUpIcon className="h-3 w-3 inline" />
        </span>
      );
    }
    
    return (
      <span className="ml-1 text-blue-600">
        {currentSortOrder === 'ASC' ? (
          <ChevronUpIcon className="h-3 w-3 inline" />
        ) : (
          <ChevronDownIcon className="h-3 w-3 inline" />
        )}
      </span>
    );
  };

  return (
    <th 
      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
      onClick={handleClick}
    >
      <div className="flex items-center">
        {label}
        {renderSortIcon()}
      </div>
    </th>
  );
};

interface HeaderProps {
  currentSortBy: string;
  currentSortOrder: string;
  onSort: (sortBy: string, sortOrder: string) => void;
}

// Standard header components for the establishment tables
export const StandardHeaders: React.FC<HeaderProps> = ({ currentSortBy, currentSortOrder, onSort }) => (
  <>
    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
      Établissement
    </th>
    <SortableHeader
      label="Date de création"
      sortKey="dateCreationEtablissement"
      currentSortBy={currentSortBy}
      currentSortOrder={currentSortOrder}
      onSort={onSort}
    />
    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
      Code NAF
    </th>
    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
      Taille
    </th>
  </>
);

export const SectorHeaders: React.FC<HeaderProps> = ({ currentSortBy, currentSortOrder, onSort }) => (
  <>
    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
      Établissement
    </th>
    <SortableHeader
      label="Date de création"
      sortKey="dateCreationEtablissement"
      currentSortBy={currentSortBy}
      currentSortOrder={currentSortOrder}
      onSort={onSort}
    />
    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
      Code NAF
    </th>
    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
      Taille
    </th>
    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
      Siège
    </th>
  </>
);

export const NonClientFromClientHeaders: React.FC<HeaderProps> = ({ currentSortBy, currentSortOrder, onSort }) => (
  <>
    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
      Établissement
    </th>
    <SortableHeader
      label="Date de création"
      sortKey="dateCreationEtablissement"
      currentSortBy={currentSortBy}
      currentSortOrder={currentSortOrder}
      onSort={onSort}
    />
    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
      Adhérents liés
    </th>
    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
      Code NAF
    </th>
    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
      Taille
    </th>
    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
      Siège
    </th>
  </>
); 